/**
 * The demo backend for Settings › Users: members and invitations held in
 * memory for the lifetime of one `MockApiClient`.
 *
 * It refuses what the real service refuses (`modules.auth.membership`), with
 * the same status codes and in the same words, because the screen's job on
 * those paths is to show the API's message — a mock that always says yes could
 * not demonstrate that it does.
 */

import { ApiError } from '@/lib/api'
import { ROLE_LABELS } from '@/api/types'

import type { UsersBackend } from './api'
import type { Invite, InviteInput, Member, RoleRef } from './types'

const INVITE_TTL_DAYS = 7
const UNASSIGNABLE = new Set(['developer_support'])

function roleRef(role: string): RoleRef {
  return { value: role, label: ROLE_LABELS[role] ?? role }
}

function shift(from: Date, days: number): string {
  return new Date(from.getTime() + days * 86_400_000).toISOString()
}

function isEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(value.trim())
}

export interface MockUsersOptions {
  now: () => Date
  /** The signed-in demo user, so the seeded list contains "you". */
  self: { email: string; name: string | null; role: string }
}

export class MockUsersBackend implements UsersBackend {
  private readonly members_: Member[]
  private readonly invites_: Invite[]
  private sequence = 0

  constructor(private readonly options: MockUsersOptions) {
    const now = options.now()
    const { self } = options
    this.members_ = [
      {
        user_id: 'usr_self',
        email: self.email,
        display_name: self.name,
        role: roleRef(self.role),
        joined_at: shift(now, -420),
        last_sign_in_at: shift(now, -0.02),
      },
      {
        user_id: 'usr_priya',
        email: 'priya.nair@example.com',
        display_name: 'Priya Nair',
        role: roleRef('administrator'),
        joined_at: shift(now, -260),
        last_sign_in_at: shift(now, -3),
      },
      {
        user_id: 'usr_tom',
        email: 'tom.whitfield@example.com',
        display_name: 'Tom Whitfield',
        role: roleRef('marketing_manager'),
        joined_at: shift(now, -95),
        last_sign_in_at: shift(now, -11),
      },
      {
        user_id: 'usr_dani',
        email: 'dani.okafor@example.com',
        display_name: 'Dani Okafor',
        role: roleRef('analyst'),
        joined_at: shift(now, -12),
        last_sign_in_at: null,
      },
    ]
    // Every tenant has an Owner. When the demo user is not one, somebody else
    // is — otherwise the rules about who may act on an Owner, and the guard
    // against removing the last one, would have nothing to act on.
    if (self.role !== 'owner') {
      this.members_.push({
        user_id: 'usr_sam',
        email: 'sam.okonkwo@example.com',
        display_name: 'Sam Okonkwo',
        role: roleRef('owner'),
        joined_at: shift(now, -500),
        last_sign_in_at: shift(now, -1),
      })
    }
    this.invites_ = [
      {
        id: 'inv_seed',
        email: 'rachel.dunne@example.com',
        role: roleRef('read_only'),
        status: 'pending',
        expires_at: shift(now, INVITE_TTL_DAYS - 2),
        created_at: shift(now, -2),
        last_sent_at: shift(now, -2),
        send_count: 1,
      },
    ]
  }

  /** The caller's own role, which is what the mock's grant rules are judged against. */
  private get actorRole(): string {
    return this.options.self.role
  }

  private nextId(prefix: string): string {
    this.sequence += 1
    return `${prefix}_${this.sequence}`
  }

  private assertMayGrant(role: string): void {
    if (UNASSIGNABLE.has(role)) {
      throw new ApiError(
        422,
        'validation_error',
        `${ROLE_LABELS[role] ?? role} is reached through audited support impersonation and is never assigned to a tenant user`,
      )
    }
    if (role === 'owner' && this.actorRole !== 'owner') {
      throw new ApiError(403, 'authorization_error', 'only an Owner may grant the Owner role')
    }
  }

  private assertNotTheLastOwner(userId: string, action: string): void {
    const owners = this.members_.filter((member) => member.role.value === 'owner')
    if (!owners.some((owner) => owner.user_id === userId)) return
    if (owners.length <= 1) {
      throw new ApiError(
        409,
        'conflict',
        `cannot ${action} the only Owner of this tenant; grant the Owner role to someone else first`,
      )
    }
  }

  private member(userId: string): Member {
    const found = this.members_.find((member) => member.user_id === userId)
    if (!found) throw new ApiError(404, 'not_found', 'no such member of this tenant')
    return found
  }

  private invite_(inviteId: string): Invite {
    const found = this.invites_.find((invite) => invite.id === inviteId)
    if (!found) throw new ApiError(404, 'not_found', 'this invitation link is not valid')
    return found
  }

  members(): Promise<Member[]> {
    return Promise.resolve(this.members_.map((member) => ({ ...member })))
  }

  changeRole(userId: string, role: string): Promise<Member> {
    return this.run(() => {
      const member = this.member(userId)
      this.assertMayGrant(role)
      if (member.role.value === 'owner' && this.actorRole !== 'owner') {
        throw new ApiError(403, 'authorization_error', 'only an Owner may change or remove another Owner')
      }
      if (role !== 'owner') this.assertNotTheLastOwner(userId, 'demote')
      member.role = roleRef(role)
      return { ...member }
    })
  }

  removeMember(userId: string): Promise<void> {
    return this.run(() => {
      const member = this.member(userId)
      if (member.role.value === 'owner' && this.actorRole !== 'owner') {
        throw new ApiError(403, 'authorization_error', 'only an Owner may change or remove another Owner')
      }
      this.assertNotTheLastOwner(userId, 'remove')
      this.members_.splice(this.members_.indexOf(member), 1)
      return undefined
    })
  }

  invites(): Promise<Invite[]> {
    return Promise.resolve(
      this.invites_.filter((invite) => invite.status === 'pending').map((invite) => ({ ...invite })),
    )
  }

  invite(input: InviteInput): Promise<Invite> {
    return this.run(() => {
      const address = input.email.trim().toLowerCase()
      if (!isEmail(address)) throw new ApiError(422, 'validation_error', `not an email address: '${input.email}'`)
      this.assertMayGrant(input.role)
      if (this.members_.some((member) => member.email.toLowerCase() === address)) {
        throw new ApiError(409, 'conflict', `${address} is already a member of this tenant`)
      }
      if (this.invites_.some((invite) => invite.email === address && invite.status === 'pending')) {
        throw new ApiError(409, 'conflict', `${address} already has a pending invitation`)
      }
      const now = this.options.now()
      const created: Invite = {
        id: this.nextId('inv'),
        email: address,
        role: roleRef(input.role),
        status: 'pending',
        expires_at: shift(now, INVITE_TTL_DAYS),
        created_at: now.toISOString(),
        last_sent_at: now.toISOString(),
        send_count: 1,
      }
      this.invites_.push(created)
      return { ...created }
    })
  }

  resendInvite(inviteId: string): Promise<Invite> {
    return this.run(() => {
      const invite = this.invite_(inviteId)
      if (invite.status !== 'pending') {
        throw new ApiError(409, 'conflict', `this invitation is ${invite.status}`)
      }
      const now = this.options.now()
      invite.last_sent_at = now.toISOString()
      invite.send_count += 1
      invite.expires_at = shift(now, INVITE_TTL_DAYS)
      return { ...invite }
    })
  }

  revokeInvite(inviteId: string): Promise<Invite> {
    return this.run(() => {
      const invite = this.invite_(inviteId)
      if (invite.status === 'accepted') {
        throw new ApiError(409, 'conflict', 'this invitation has already been accepted')
      }
      invite.status = 'revoked'
      return { ...invite }
    })
  }

  /** Refusals arrive as a rejected promise, the way the HTTP client's do. */
  private run<T>(body: () => T): Promise<T> {
    try {
      return Promise.resolve(body())
    } catch (error) {
      return Promise.reject(error)
    }
  }
}
